import React, { useEffect, useRef, useState } from "react";
import { Timer, Pause, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const RE = /(\d+(?:\.\d+)?)(?:\s*(?:-|–|to)\s*(\d+))?\s*(hours?|hrs?|minutes?|mins?|min|seconds?|secs?)\b/gi;

export function extractTimers(text) {
  const out = [];
  let m;
  while ((m = RE.exec(text || "")) !== null) {
    const n = Number(m[2] || m[1]);
    const unit = m[3].toLowerCase();
    const secs = unit.startsWith("h") ? n * 3600 : unit.startsWith("s") ? n : n * 60;
    if (secs >= 30 && secs <= 6 * 3600) out.push({ label: m[0].trim(), secs: Math.round(secs) });
  }
  return out.filter((t, i, a) => a.findIndex((x) => x.secs === t.secs) === i).slice(0, 3);
}

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [0, 0.35, 0.7].forEach((delay) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 880; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.25, ctx.currentTime + delay);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.3);
      o.start(ctx.currentTime + delay); o.stop(ctx.currentTime + delay + 0.3);
    });
  } catch { /* audio not available */ }
  if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function StepTimer({ label, secs, testId }) {
  const [left, setLeft] = useState(secs);
  const [running, setRunning] = useState(false);
  const endAt = useRef(null);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      const remain = Math.max(0, Math.round((endAt.current - Date.now()) / 1000));
      setLeft(remain);
      if (remain === 0) { setRunning(false); beep(); toast.success(`Ding! ${label} is up 🔔`); }
    }, 250);
    return () => clearInterval(id);
  }, [running, label]);

  const start = () => { endAt.current = Date.now() + left * 1000; setRunning(true); };
  const pause = () => setRunning(false);
  useEffect(() => {
    const onVoice = (e) => {
      if (e.detail === "timer_start" && !running && left > 0) start();
      if (e.detail === "timer_pause" && running) pause();
    };
    window.addEventListener("sorted-timer", onVoice);
    return () => window.removeEventListener("sorted-timer", onVoice);
  });
  const reset = () => { setRunning(false); setLeft(secs); };
  const done = left === 0;
  const pct = ((secs - left) / secs) * 100;

  return (
    <div className={`rounded-2xl border-2 p-3 flex items-center gap-3 ${done ? "bg-sorted-sage border-sorted-sage" : "bg-white border-[#FDECEF]"}`} data-testid={testId}>
      <span className="w-9 h-9 rounded-full bg-[#FFF6F1] flex items-center justify-center text-sorted-rose"><Timer size={17} strokeWidth={2.5} /></span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-sorted-slate capitalize truncate">{label}</p>
        <p className="font-heading font-bold text-xl text-sorted-ink leading-none" data-testid={`${testId}-left`}>{done ? "Done!" : fmt(left)}</p>
        <div className="h-1 bg-[#FDECEF] rounded-full mt-1.5 overflow-hidden"><div className="h-full bg-sorted-rose rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
      </div>
      {!done && (
        <button onClick={running ? pause : start} data-testid={`${testId}-toggle`} aria-label={running ? "Pause" : "Start"}
          className="w-10 h-10 rounded-full bg-sorted-rose text-white flex items-center justify-center">
          {running ? <Pause size={16} strokeWidth={2.5} /> : <Play size={16} strokeWidth={2.5} />}
        </button>
      )}
      {(left !== secs || done) && (
        <button onClick={reset} data-testid={`${testId}-reset`} aria-label="Reset" className="w-9 h-9 rounded-full bg-muted text-sorted-ink flex items-center justify-center"><RotateCcw size={14} strokeWidth={2.5} /></button>
      )}
    </div>
  );
}
